import { useCallback, useEffect, useRef, useState } from 'react';

import howler from 'howler';

import { devError } from '../utils/log';

export interface SoundOptions {
  loop?: boolean;
  singleton?: boolean;
  delay?: number;
}

export function useSounds<T extends string>(allSounds: Record<T, string>) {
  const soundsMap = useRef<Map<T, howler.Howl> | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);

  const loadSounds = useCallback(async () => {
    if (soundsMap.current) {
      return;
    }
    setLoading(true);
    soundsMap.current = new Map<T, howler.Howl>();
    const sounds = Object.entries(allSounds) as [T, string][];
    const promises = sounds.map(([soundType, soundPath]) => {
      const sound = new howler.Howl({
        src: [soundPath],
        preload: true,
        onload: () => {
          soundsMap.current?.set(soundType, sound);
          if (soundType === 'backgroundMusic') {
            sound.volume(0.3);
          }
        },
        onloaderror: (id, error) => {
          devError(`Error loading sound ${soundType}, id: ${id}`, error);
        },
      });

      return sound;
    });

    await Promise.all(promises);
    setLoading(false);
  }, [allSounds]);

  const playSound = (soundType: T, options?: SoundOptions) => {
    const { loop = false, singleton = false, delay = 0 } = options || {};

    const sound = soundsMap.current?.get(soundType);
    if (singleton && sound?.playing()) {
      return;
    }
    sound?.loop(loop);
    if (sound) {
      setTimeout(() => {
        sound.play();
      }, delay);
    }
  };

  useEffect(() => {
    void loadSounds();
  }, [loadSounds]);

  return {
    isLoaded: soundsMap !== null && !isLoading,
    isLoading,
    playSound,
  };
}
